import React, { useEffect, useState } from "react";
import { useAuth } from "../../utils/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import MuiAlert from "@mui/material/Alert";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import {
  cardOutline,
  headingFonts,
  mainButtonStyles,
  noOutline,
  textFieldStyles,
} from "../../../Styles/Styles";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editCategoryLabel, setEditCategoryLabel] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setLoading(true);
      return;
    }

    if (user.role !== "superAdmin") {
      navigate("/dashboard");
      return;
    }

    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories`
        );
        if (!response.ok) throw new Error("Failed to fetch categories");
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [user, navigate]);

  const handleFileChange = (e) => setImageFile(e.target.files[0]);

  const handleEditFileChange = (e) => setEditImageFile(e.target.files[0]);

  const handleAddCategory = async () => {
    setIsSubmitting(true);
    if (!newCategory.trim() || !imageFile) return;

    try {
      const formData = new FormData();
      formData.append("label", newCategory);
      formData.append("slug", newCategory.toLowerCase().replace(/\s+/g, "-"));
      formData.append("image", imageFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories/newcategory`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Failed to add category");

      const category = await response.json();
      setCategories([...categories, category]);
      setNewCategory("");
      setImageFile(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCategory = async (id) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("label", editCategoryLabel);
      if (editImageFile) formData.append("image", editImageFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories/updatecategory/${id}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Failed to update category");

      const updatedCategory = await response.json();
      setCategories(
        categories.map((cat) =>
          cat._id === id
            ? { ...cat, label: editCategoryLabel, image: updatedCategory.image }
            : cat
        )
      );
      setEditCategoryId(null);
      setEditImageFile(null);
      setEditCategoryLabel("");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories/${id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (!response.ok) throw new Error("Failed to delete category");

      setCategories(categories.filter((cat) => cat._id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditClick = (category) => {
    if (editCategoryId !== category._id) {
      setEditCategoryId(category._id);
      setEditCategoryLabel(category.label);
    } else {
      setEditCategoryId(null);
      setEditCategoryLabel(null);
    }
  };

  const handleCloseSnackbar = () => setError(null);

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Categories Management - Listed.ky</title>
        <meta name="description" content="Categories Management - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />

      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        Categories Management
      </Typography>

      {error && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity="error">
            {error}
          </Alert>
        </Snackbar>
      )}

      {/* Categories Table */}
      <TableContainer sx={{ ...cardOutline }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headingFonts }}>Image</TableCell>
              <TableCell sx={{ ...headingFonts }}>Name</TableCell>
              <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat) => (
              <>
                <TableRow key={cat._id}>
                  <TableCell>
                    <img
                      src={cat.image}
                      alt={cat.label}
                      width="60"
                      height="60"
                    />
                  </TableCell>
                  <TableCell sx={{ ...headingFonts }}>{cat.label}</TableCell>
                  <TableCell>
                    {/* Edit Button */}
                    <IconButton
                      onClick={() => handleEditClick(cat)}
                      sx={{
                        color: "var(--primary-color)",
                        ...noOutline,
                      }}
                    >
                      <EditIcon />
                    </IconButton>

                    {/* Delete Button */}
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteCategory(cat._id)}
                      sx={{ ...noOutline }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {editCategoryId === cat._id && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: {
                            xs: "100%",
                            sm: "80%",
                            md: "50%",
                            lg: "50%",
                          },
                        }}
                      >
                        <TextField
                          fullWidth
                          margin="normal"
                          label="Category Name"
                          value={editCategoryLabel}
                          onChange={(e) => setEditCategoryLabel(e.target.value)}
                          sx={{ ...textFieldStyles }}
                        />
                        <TextField
                          label="Category Image"
                          type="file"
                          variant="outlined"
                          fullWidth
                          onChange={handleEditFileChange}
                          required
                          margin="normal"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{ ...textFieldStyles }}
                        />
                        <Box sx={{ display: "flex" }}>
                          <Button
                            className="main-button-style"
                            onClick={() => handleUpdateCategory(cat._id)}
                            sx={{ mt: 2, ...mainButtonStyles }}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span>Updating...</span>
                                <CircularProgress
                                  size={20}
                                  sx={{ color: "white" }}
                                />
                              </>
                            ) : (
                              <>
                                <span>Update</span>
                                <ArrowForwardOutlinedIcon />
                              </>
                            )}
                          </Button>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Add Category Section */}
      <Box
        sx={{
          p: 2,
          borderRadius: "4px",
          mb: 5,
          mt: 3,
          border: "1px solid #ddd", // Light border by default
          boxShadow: "none",
          width: {
            xs: "100%",
            sm: "80%",
            md: "50%",
            lg: "50%",
          },
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
          Add New Category
        </Typography>

        <TextField
          fullWidth
          margin="normal"
          label="Category Name"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          sx={{ ...textFieldStyles }}
        />
        <TextField
          label="Category Image"
          type="file"
          variant="outlined"
          fullWidth
          onChange={handleFileChange}
          required
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ ...textFieldStyles }}
        />
        <Box sx={{ display: "flex" }}>
          <Button
            className="main-button-style"
            onClick={handleAddCategory}
            sx={{ mt: 2, ...mainButtonStyles }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span>Submitting...</span>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </>
            ) : (
              <>
                <span>Submit</span>
                <ArrowForwardOutlinedIcon />
              </>
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryManagement;
